import { Injectable } from '@angular/core';
import constant from 'lodash/constant';
import { firstValueFrom } from 'rxjs';
import { FormService } from 'src/app/services/form.service';
import { MessageService } from 'primeng/api';
import { TableCommunicationService } from './table-communication.service';
import { GridApi } from 'ag-grid-enterprise';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/auth/auth.service';

interface RowChange {
  original: any; // Consider specifying a more precise type
  new: any; // Consider specifying a more precise type
  selectedColumn: string;
}

interface ChangedDataType {
  [key: string]: any; // Ideally, replace 'any' with a more specific type based on your data
}

type ChangesPreview = { [key: string]: RowChange };

export interface BulkEditFailure {
  id: number;
  error: string;
}

export interface BulkEditResult {
  updated: number[];
  failed: BulkEditFailure[];
}

/** Must not exceed MAX_BULK_UPDATE_ITEMS in api/src/common/service/bulk-update.helper.ts. */
const MAX_BULK_UPDATE_ITEMS = 500;

/**
 * Entities whose API bulk-update runs the full per-record update automations (env flags,
 * promotion calls). Those rows are far slower than a plain save, so a 500-row request would
 * outlive the load balancer's idle timeout and get retried as a whole.
 */
const AUTOMATED_BULK_UPDATE_ENTITIES = ['items', 'challenges'];
const AUTOMATED_BULK_UPDATE_CHUNK_SIZE = 25;

/**
 * Chunk-level retry for transient failures.
 *
 * Only network/429/5xx failures are retried — a 400 from an env-permission check or a 404 for a
 * deleted record will fail identically every time, so retrying those just delays the report.
 */
const BULK_RETRY_ATTEMPTS = 3;
const BULK_RETRY_BASE_DELAY_MS = 1000;

@Injectable({
  providedIn: 'root'
})
export class AgGridEditService {

  constructor(
    private formService: FormService,
    private messageService: MessageService,
    private tableCommunicationService: TableCommunicationService,
    private route: ActivatedRoute,
    private authService: AuthService,
  ) { }

  convertToMongoDate(dateString: string) {
    // Convert the date string to a JavaScript Date object
    const jsDate = new Date(Date.parse(dateString));

    // If the date is invalid, return null or handle accordingly
    if (isNaN(jsDate.getTime())) {
      return null;
    }

    return jsDate;
  }

  convertValueForDB(field: any, newValue: any, colDef: any) {
    // Special handling for boolean values - false is a valid value
    if (newValue === false) {
      return false;
    }
    
    if (!newValue) {
      return null;
    }
    switch (field) {
      case 'start':
      case 'end':
      case 'reReleaseStart':
      case 'reReleaseEnd':
        return this.convertToMongoDate(newValue);
        break;
      case 'collection_ref':
        if (Array.isArray(newValue)) {
          return newValue.map((obj: any) => { return { id: obj.id, name: obj.name, } });
        } else {
          // console.error('newValue is not an array:', newValue);
          // handle the case where newValue is not an array as needed
          return newValue;
        }
        break;
      // case 'collection_ref':
      // return newValue.map((obj: any) => { return { id: obj.id, name: obj.name, } } );
      // break;
      default:
        if (colDef.cellEditorParams && colDef.cellEditorParams.objectValue && colDef.cellEditorParams.optionValue) {
          // newValue may already be a primitive (e.g. from bulk edit where p-dropdown
          // binds [optionValue] and stores just the id string rather than the full object)
          return Array.isArray(newValue) ?
            newValue.map((obj: Record<string, any>) => typeof obj === 'object' && obj !== null ? obj[colDef.cellEditorParams.optionValue] : obj) :
            (typeof newValue === 'object' && newValue !== null ?
              newValue[colDef.cellEditorParams.optionValue] :
              newValue);
        } else {
          return newValue;
        }
        break;
    }
  }

  /** Rows per bulk-update request for the given entity. */
  getBulkChunkSize(entity: string, isDynamicEntity: boolean = false): number {
    return !isDynamicEntity && AUTOMATED_BULK_UPDATE_ENTITIES.includes(entity)
      ? AUTOMATED_BULK_UPDATE_CHUNK_SIZE
      : MAX_BULK_UPDATE_ITEMS;
  }

  async submitEditRow(value: any, entity: string, id: number, isDynamicEntity: boolean = false): Promise<any> {
    const userResult = this.authService.getSocialUser();
    const payloadWithSource = {
      ...value,
      userData: {
        name: userResult.currentUser.name,
        email: userResult.currentUser.email,
        id: userResult.currentUser.id,
        editSource: 'ag-grid-table',
        timestamp: new Date()
      }
    };

    let formURL: string = `${entity}/update/${id}`;
    if (isDynamicEntity) {
      formURL = `dynamic-entities/manager/update/${entity}/${id}`;
    }

    const maxRetries = 4;
    let delay = 2000;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await firstValueFrom(this.formService.submitForm(payloadWithSource, formURL, true));
      } catch (err: any) {
        if (err?.status === 429 && attempt < maxRetries) {
          await new Promise(res => setTimeout(res, delay));
          delay *= 2;
          continue;
        }
        console.log('err', err);
        this.messageService.add({
          sticky: false,
          severity: 'error',
          summary: `ERROR updating ${entity} ID: ${id}`,
          detail: this.formatErrorMessage(err),
        });
        throw err;
      }
    }
  }


  /**
 * Submits a row change to the server, with support for environment-specific modifications.
 *
 * @param recordId The ID of the record to update.
 * @param changedRowsMap A map of record IDs to their changed data.
 * @param api An API object for interacting with the grid.
 * @param entity The entity type associated with the row.
 * @param changesPreview An optional map of changes for preview purposes.
 * @param rowID The ID of the row, if applicable (for legacy bulk edit support).
 * @param storeEnv The store environment, if environment-specific changes are needed.
 * @param isDynamicEntity Whether this is a dynamic entity.
 * @param skipRefresh Whether to skip automatic refresh (used in bulk operations).
 * @returns A promise that resolves to true on success, or rejects with an error.
 */
  submitRow(
    recordId: number | undefined,
    changedRowsMap: Map<number, ChangedDataType> | undefined,
    api: any, // Consider defining a more precise type for the API
    entity: string,
    changesPreview: ChangesPreview | null = null,
    rowID: number | null = null,
    storeEnv: string | null = null,
    isDynamicEntity: boolean = false,
    skipRefresh: boolean = false
  ): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        if (changedRowsMap && recordId !== undefined) {
          var changedData = changedRowsMap.get(recordId);
          if (changedData) {
            if (storeEnv) {
              const exceptionKeys = ['id', '_id', 'userData', 'name'];
              const modifiedData = Object.keys(changedData).reduce<ChangedDataType>((acc, key) => {
                if (exceptionKeys.includes(key)) {
                  acc[key] = changedData ? changedData[key] : null; // Directly copy the value for exception keys
                } else {
                  // For other keys, nest under storeEnv
                  acc[key] = { [storeEnv]: changedData ? changedData[key] : null };
                }
                return acc;
              }, {}); // The initial value of the accumulator is an empty object with type ChangedDataType
              changedData = modifiedData;
            }

            await this.submitEditRow(changedData, entity, recordId, isDynamicEntity);
            changedRowsMap.delete(recordId);
            
            // Get the current row index for UI updates (optional)
            const rowNode = api.getRowNode(String(recordId));
            const rowIndex = rowNode?.rowIndex;
            if (rowIndex !== undefined) {
              this.tableCommunicationService.submitSaveRow(rowIndex);
            }
          } else if (!skipRefresh) {
            // Only refresh if not in bulk edit mode
            api.refreshServerSide();
          }
          resolve(true);
        } else if (changesPreview && rowID) {
          const changesData = changesPreview[rowID];
          if (changesData) {
            const updateData = { [changesData.selectedColumn]: changesData.new };
            await this.submitEditRow(updateData, entity, rowID, isDynamicEntity);
            resolve(true);
          }
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  revertChanges(
    api: GridApi,
    changedRows: Map<number, any>,
    originalData: Map<string, any>,
    changedRecordIds: number[]
  ) {
    for (const recordId of changedRecordIds) {
      const rowChanges = changedRows.get(recordId);
      const rowNode = api.getRowNode(String(recordId));

      if (!rowNode) continue;

      for (const field in rowChanges) {
        const originalCellValue = originalData.get(`${recordId}-${field}`);
        rowNode.data[field] = originalCellValue;
        // Clean up originalData as well
        originalData.delete(`${recordId}-${field}`);
      }
      changedRows.delete(recordId);
    }
    // Redraw rows to reflect the changes in the UI
    api.redrawRows();
  }


  /**
   * Sends one batch of row changes to the server as a SINGLE request.
   *
   * Replaces the previous approach of one PATCH per row. A 141-row bulk edit issued 141 requests
   * and tripped the API's 100-requests-per-minute per-user throttler, so every row past the 100th
   * came back 429 — the "Too many attempts" toasts, and rows silently left unedited.
   *
   * @returns the ids that were actually persisted, and the ones that failed with a reason.
   */
  async submitBulkEdit(
    api: GridApi,
    changesPreview: { [key: string]: { original: any, new: any, selectedColumn: string } },
    selectedColumn: { id: string, name: string, type: string },
    changedRowsMap: Map<number, any>,
    entity: string,
    isDynamicEntity: boolean = false,
    skipRefresh: boolean = false
  ): Promise<BulkEditResult> {
    // Step 1: reflect the new values in the grid so the UI matches what we are about to save.
    for (const [rowID, change] of Object.entries(changesPreview)) {
      const rowNode = api.getRowNode(rowID);
      if (rowNode) {
        rowNode.setDataValue(selectedColumn.id, change.new);
      }
    }

    // Step 2: let AG Grid's value setters flush before we read the pending changes back out.
    await new Promise(resolve => setTimeout(resolve, 50));

    // Step 3: build one { id, dto } entry per row. Per-row DTOs are required because edit types
    // like find-and-replace, append and add/remove derive each row's new value from its own
    // current value, so the rows do not share a single payload.
    const colDef = api.getColumnDef(selectedColumn.id) ?? {};
    const updates: { id: number, dto: any }[] = [];
    for (const rowID of Object.keys(changesPreview)) {
      const recordId = Number(rowID);
      const change = changesPreview[rowID];
      // onCellValueChanged only fires when the value actually differs, so a row whose new value
      // already matches its current one leaves no entry in changedRowsMap. Fall back to the
      // previewed change, converted the same way the grid's own change handler converts it.
      const pending = changedRowsMap.get(recordId);
      const dto = pending ?? {
        [change.selectedColumn]: this.convertValueForDB(change.selectedColumn, change.new, colDef),
      };
      updates.push({ id: recordId, dto });
    }

    if (!updates.length) {
      return { updated: [], failed: [] };
    }

    const result = await this.sendBulkUpdate(entity, updates, isDynamicEntity);

    // Only clear rows the server confirmed; a failed row keeps its pending change so a retry
    // still has something to send.
    for (const id of result.updated) {
      changedRowsMap.delete(id);
      const rowNode = api.getRowNode(String(id));
      if (rowNode?.rowIndex !== undefined && rowNode?.rowIndex !== null) {
        this.tableCommunicationService.submitSaveRow(rowNode.rowIndex);
      }
    }

    for (const failure of result.failed) {
      this.messageService.add({
        sticky: false,
        severity: 'error',
        summary: `ERROR updating ${entity} ID: ${failure.id}`,
        detail: failure.error,
      });
    }

    if (!skipRefresh) {
      if (api.refreshServerSide) {
        api.refreshServerSide();
      } else {
        api.redrawRows();
        api.refreshCells({ force: true });
      }
    }

    return result;
  }

  /**
   * POSTs one bulk-update request, chunked to the API's per-request ceiling.
   *
   * Chunks are sent sequentially: the point is to keep the request count low, and sequential
   * chunks also keep the caller's progress reporting monotonic.
   */
  private async sendBulkUpdate(
    entity: string,
    updates: { id: number, dto: any }[],
    isDynamicEntity: boolean
  ): Promise<BulkEditResult> {
    const userResult = this.authService.getSocialUser();
    const userData = {
      name: userResult.currentUser.name,
      email: userResult.currentUser.email,
      id: userResult.currentUser.id,
      editSource: 'ag-grid-table-bulk-edit',
      timestamp: new Date(),
    };

    const url = isDynamicEntity
      ? `dynamic-entities/manager/bulk-update/${entity}`
      : `${entity}/bulk-update`;

    const aggregate: BulkEditResult = { updated: [], failed: [] };

    const chunkSize = this.getBulkChunkSize(entity, isDynamicEntity);
    for (let i = 0; i < updates.length; i += chunkSize) {
      const chunk = updates.slice(i, i + chunkSize);
      let lastError: any = null;

      for (let attempt = 1; attempt <= BULK_RETRY_ATTEMPTS; attempt++) {
        try {
          const response: any = await firstValueFrom(
            this.formService.submitForm({ updates: chunk, userData }, url, true)
          );
          aggregate.updated.push(...(response?.updated ?? []));
          aggregate.failed.push(...(response?.failed ?? []));
          lastError = null;
          break;
        } catch (err: any) {
          lastError = err;
          if (attempt < BULK_RETRY_ATTEMPTS && this.isTransientError(err)) {
            await new Promise(res => setTimeout(res, this.retryDelayMs(err, attempt)));
            continue;
          }
          break;
        }
      }

      if (lastError) {
        // The whole chunk failed and is not worth retrying again. Report every id in it as
        // failed rather than letting the caller report a success it cannot verify.
        const message = this.formatErrorMessage(lastError);
        aggregate.failed.push(...chunk.map(u => ({ id: u.id, error: message })));
      }
    }

    return aggregate;
  }

  /** A failure worth retrying: no response at all, rate limited, timed out, or a server fault. */
  private isTransientError(err: any): boolean {
    const status = err?.status;
    return status === 0 || status === 408 || status === 429 || (status >= 500 && status <= 599);
  }

  /** Exponential backoff, but honour Retry-After when the server sends one. */
  private retryDelayMs(err: any, attempt: number): number {
    const retryAfter = Number(err?.headers?.get?.('Retry-After'));
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      return retryAfter * 1000;
    }
    return BULK_RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
  }

  /**
   * Formats error messages for better user experience
   * @param err The error object from HTTP responses
   * @returns A formatted error message string
   */
  private formatErrorMessage(err: any): string {
    // Handle different error response formats
    if (err.error && typeof err.error === 'object') {
      // Check if it's a structured error response with a message
      if (err.error.message) {
        let formattedMessage = err.error.message;
        
        // Add conflicts information if available
        if (err.error.conflicts && Array.isArray(err.error.conflicts)) {
          const conflictsInfo = err.error.conflicts
            .map((conflict: any) => `${conflict.key}: ${Array.isArray(conflict.currentValue) ? conflict.currentValue.join(', ') : conflict.currentValue}`)
            .join('; ');
          
          if (conflictsInfo) {
            formattedMessage += ` (Conflicts: ${conflictsInfo})`;
          }
        }
        
        return formattedMessage;
      }
      
      // If no message but has error property
      if (err.error.error) {
        return err.error.error;
      }
    }
    
    // Handle specific HTTP status codes
    if (err.status === 403) {
      return err.error?.error || err.statusText || 'Forbidden - Access denied';
    }
    
    if (err.status === 400) {
      return err.error?.message || err.statusText || 'Bad Request';
    }
    
    if (err.status === 404) {
      return err.error?.message || err.statusText || 'Not Found';
    }
    
    if (err.status === 500) {
      return err.error?.message || err.statusText || 'Internal Server Error';
    }
    
    // Fallback: return the full error as JSON for debugging
    return JSON.stringify(err);
  }

}
